// React
import React, { useEffect, useState, Fragment } from 'react'

// Components
import AddressFields from 'back_office/modules/addresses/components/AddressFields'
import { UserAddressesSelect } from './UserAddressesSelect'
import Span from 'ui/elements/Span'
import A from 'ui/elements/A'
import Div from 'ui/elements/Div'
import Label from 'ui/elements/Label'

// Hooks
import { useTranslation } from 'shared/utils/useTranslation'
import { useOrm } from 'shared/utils/useOrm'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { useSettings } from 'shared/utils/useSettings'

// Shared
import { addressFieldsOrder } from 'back_office/modules/addresses/components/addressFieldsOrder'
import { featureDisabled, FeatureFlags } from 'shared/utils/features'
import { fetchUserAddressesRequest } from './requests'

export const DeliveryFulfillmentForm = () => {
  const [userAddresses, setUserAddresses] = useState([])
  const [isUserAuthenticated, setIsUserAuthenticated] = useState(undefined)

  const form = useFormikContext()
  const { t, tOrEmptyString, Trans } = useTranslation('user')

  const cartId = useSelector((state) => state.client.cartId)

  const cart = useOrm('carts', (carts) => carts.getById(cartId || window.booqableOptions.cart?.id))
  const deliveryCountries = useSettings('store.delivery_country_names', { default: [] })
  const countries = useOrm('countries', (collection) => {
    return collection.getAll({ ...(deliveryCountries.length ? { name: deliveryCountries } : {}) }).toArray()
  })
  const provinces = useOrm('provinces', (provinces) => {
    return provinces.getAll().toArray()
  })

  const isAuthEnabled = useSettings('user.auth_enabled')
  const isAuthAvailable = new URL(window.location.origin).origin === new URL(window.booqableOptions.apiURL).origin

  const authUriBase = cart?.user_login_url ?? '/customers/login'
  const authRedirectUriParamValue = `${window.location.origin}?${encodeURIComponent(
    'open-picker=true&fulfillment-tab=delivery'
  )}`
  const authUri = `${authUriBase}?redirect_uri=${authRedirectUriParamValue}`

  useEffect(() => {
    const fetchUserAddresses = async () => {
      try {
        const { data } = await fetchUserAddressesRequest()

        setIsUserAuthenticated(true)
        setUserAddresses(data.properties)
      } catch {
        setIsUserAuthenticated(false)
      }
    }

    if (!cart) return
    if (!isAuthEnabled) return
    if (!isAuthAvailable) return

    fetchUserAddresses()
  }, [cart?.id, isAuthEnabled, isAuthAvailable])

  return (
    <Fragment>
      {isUserAuthenticated && userAddresses.length > 0 && (
        <Div modifiers="margin-bottom-md">
          <Label modifiers="margin-bottom-xs">{t('user.deliveries.choose_saved_address')}</Label>
          <UserAddressesSelect addresses={userAddresses} />
        </Div>
      )}
      <AddressFields
        defaultFormLayout={addressFieldsOrder()}
        skipValidation={featureDisabled(FeatureFlags.NEW_ADDRESS_VALIDATION)}
        invalidExistingAddressWarning={t('user.deliveries.invalid_existing_address_warning')}
        fieldTranslationPrefix="user.deliveries"
        errorKey="user.public_errors.invalid"
        selectPlaceholder={tOrEmptyString('user.checkout.select_placeholder')}
        initialValues={form.initialValues}
        countries={countries}
        provinces={provinces}
        addressIdentifier="delivery_address"
        fieldDebounce={750}
        hideCountryWhenSingle
      />
      {isUserAuthenticated === false && isAuthEnabled && isAuthAvailable && (
        <Span modifiers="display-inline-block font-size-md margin-top-md">
          <Trans
            i18nKey="user.deliveries.login"
            components={{
              Link: <A modifiers="font-weight-bold font-size-md text-blue text-decoration-none" href={authUri} />
            }}
          />
        </Span>
      )}
    </Fragment>
  )
}
